import React from "react";
import { Container, Typography, Box, Chip, Button, Dialog, DialogTitle, DialogContent, DialogActions } from "@material-ui/core";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { useHistory } from "react-router-dom";
import Spinner from "../components/Spinner/Spinner";
import styles from "../components/Invoices-DataGrid/Invoices.module.css";
import { fetchActivityLogs, fetchInvoice as fetchInvoiceApi } from "../api";

const activityLogAdminEmail = process.env.REACT_APP_ACTIVITY_LOG_ADMIN_EMAIL || "paul@example.com";

const actionLabels = {
  invoice_create: "Invoice Created",
  invoice_update: "Invoice Updated",
  invoice_soft_delete: "Invoice Deleted",
  invoice_restore: "Invoice Restored",
  invoice_permanent_delete: "Invoice Permanently Deleted",
  invoice_payment_add: "Payment Recorded",
  invoice_payment_update: "Payment Edited",
  invoice_payment_remove: "Payment Removed",
  quote_create: "Quote Created",
  quote_update: "Quote Updated",
  quote_decline: "Quote Declined",
  quote_delete: "Quote Deleted",
  quote_restore: "Quote Restored",
  quote_permanent_delete: "Quote Permanently Deleted",
};
const actionFilterOptions = Object.entries(actionLabels).map(([value, label]) => ({
  value,
  label,
}));

const getActionFilterValue = (filterModel) => {
  const actionFilter = (filterModel?.items || []).find(
    (item) => item.field === "action" && item.value
  );
  return Array.isArray(actionFilter?.value)
    ? actionFilter.value
    : actionFilter?.value || "";
};

const buildDescription = (row) => {
  if (row.entityType === "invoice") {
    const client = row.metadata?.client || "Unknown client";
    switch (row.action) {
      case "invoice_create":
        return `Invoice created for ${client} (${row.metadata?.total ? `$${Number(row.metadata.total).toFixed(2)}` : "no total"})`;
      case "invoice_update":
        return `Invoice updated for ${client}${row.metadata?.status ? ` • Status: ${row.metadata.status}` : ""}`;
      case "invoice_soft_delete":
        return `Invoice deleted for ${client}`;
      case "invoice_restore":
        return `Invoice restored for ${client}`;
      case "invoice_permanent_delete":
        return `Invoice permanently deleted for ${client}`;
      case "invoice_payment_add":
        return `Recorded payment of $${Number(row.metadata?.amount || 0).toFixed(2)} for ${client}`;
      case "invoice_payment_update":
        return `Edited payment for ${client} from $${Number(row.metadata?.previous?.amountPaid || 0).toFixed(2)} to $${Number(row.metadata?.current?.amountPaid || 0).toFixed(2)}`;
      case "invoice_payment_remove":
        return `Removed payment of $${Number(row.metadata?.amount || 0).toFixed(2)} for ${client}`;
      default:
        return `${row.action} (${client})`;
    }
  }
  if (row.entityType === "quote") {
    const quoteNumber = row.metadata?.quoteNumber || "Unknown quote";
    const address = row.metadata?.address ? ` for ${row.metadata.address}` : "";
    switch (row.action) {
      case "quote_create":
        return `Quote ${quoteNumber} created${address}`;
      case "quote_update":
        return `Quote ${quoteNumber} updated${address}`;
      case "quote_decline":
        return `Quote ${quoteNumber} declined${address}`;
      case "quote_delete":
        return `Quote ${quoteNumber} deleted${address}`;
      case "quote_restore":
        return `Quote ${quoteNumber} restored${address}`;
      case "quote_permanent_delete":
        return `Quote ${quoteNumber} permanently deleted${address}`;
      default:
        return `${row.action} (${quoteNumber})`;
    }
  }
  return row.action;
};

const buildColumns = (openDetails) => [
  {
    field: "createdAt",
    headerName: "Timestamp",
    type: "dateTime",
    flex: 1.4,
    minWidth: 180,
    valueGetter: (params) => (params.value ? new Date(params.value) : null),
  },
  {
    field: "userEmail",
    headerName: "User",
    flex: 1,
    minWidth: 160,
    valueGetter: (params) => params.value || "Unknown",
  },
  {
    field: "action",
    headerName: "Action",
    type: "singleSelect",
    valueOptions: actionFilterOptions.map((opt) => opt.value),
    flex: 1.2,
    minWidth: 180,
    renderCell: (params) => (
      <Chip
        size="small"
        color="primary"
        label={actionLabels[params.value] || params.value}
        style={{ backgroundColor: "#e0f2fe", color: "#0369a1" }}
      />
    ),
  },
  {
    field: "entityType",
    headerName: "Entity",
    flex: 0.8,
    minWidth: 100,
  },
  {
    field: "description",
    headerName: "Summary",
    flex: 2,
    minWidth: 240,
    renderCell: (params) => (
      <Typography
        variant="body2"
        style={{ whiteSpace: "normal", lineHeight: 1.4 }}
      >
        {buildDescription(params.row)}
      </Typography>
    ),
  },
  {
    field: "details",
    headerName: "Details",
    sortable: false,
    filterable: false,
    width: 130,
    renderCell: (params) => (
      <Button
        size="small"
        variant="contained"
        color="primary"
        onClick={(e) => {
          e.stopPropagation();
          openDetails(params.row);
        }}
      >
        View
      </Button>
    ),
  },
];

const ActivityLogPage = () => {
  const history = useHistory();
  const user = React.useMemo(
    () => JSON.parse(localStorage.getItem("profile")),
    []
  );
  const isAuthorized = user?.result?.email === activityLogAdminEmail;
  const [rows, setRows] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [invoiceCache, setInvoiceCache] = React.useState({});
  const [invoiceLoading, setInvoiceLoading] = React.useState({});
  const [detailRow, setDetailRow] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: 25,
  });
  const [filterModel, setFilterModel] = React.useState({ items: [] });
  const actionFilter = React.useMemo(
    () => getActionFilterValue(filterModel),
    [filterModel]
  );

  React.useEffect(() => {
    if (!isAuthorized) {
      if (user) history.push("/dashboard");
      else history.push("/login");
      return;
    }
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const { data } = await fetchActivityLogs({
          page: paginationModel.page,
          limit: paginationModel.pageSize,
          action: actionFilter,
        });
        if (!active) return;
        setRows(data?.data || []);
        setRowCount(data?.total || 0);
      } catch (error) {
        console.log(error);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isAuthorized, history, user, paginationModel.page, paginationModel.pageSize, actionFilter]);

  const handleFilterModelChange = React.useCallback((nextFilterModel) => {
    setFilterModel(nextFilterModel);
    setPaginationModel((prev) => ({ ...prev, page: 0 }));
  }, []);

  const ensureInvoiceLoaded = React.useCallback(
    async (invoiceId) => {
      if (!invoiceId || invoiceCache[invoiceId] || invoiceLoading[invoiceId]) return;
      setInvoiceLoading((prev) => ({ ...prev, [invoiceId]: true }));
      try {
        const { data } = await fetchInvoiceApi(invoiceId);
        setInvoiceCache((prev) => ({ ...prev, [invoiceId]: data }));
      } catch (error) {
        console.log(error);
      } finally {
        setInvoiceLoading((prev) => {
          const next = { ...prev };
          delete next[invoiceId];
          return next;
        });
      }
    },
    [invoiceCache, invoiceLoading]
  );

  const handleOpenDetails = React.useCallback(
    (row) => {
      setDetailRow(row);
      setDetailOpen(true);
      if (row?.entityType === "invoice") {
        ensureInvoiceLoaded(row.entityId);
      }
    },
    [ensureInvoiceLoaded]
  );

  const handleCloseDetails = () => {
    setDetailOpen(false);
  };

  if (!isAuthorized) return null;

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        style={{ paddingTop: 40 }}
      >
        <Spinner />
      </Box>
    );
  }

  return (
    <div className={styles.pageLayout}>
      <Container style={{ maxWidth: 1400 }}>
        <div className={styles.buttons}>
          <Typography variant="h4" component="h1" style={{ paddingTop: 10 }}>
            Activity Log
          </Typography>
        </div>
        <div className={styles.gridLayout}>
          <DataGrid
            autoHeight
            rows={rows}
            columns={buildColumns(handleOpenDetails)}
            getRowId={(row) => row._id}
            components={{ Toolbar: GridToolbar }}
            rowCount={rowCount}
            paginationMode="server"
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            filterModel={filterModel}
            onFilterModelChange={handleFilterModelChange}
            pageSizeOptions={[25, 50, 100]}
            loading={loading}
            sortingOrder={["desc", "asc"]}
          />
        </div>

        <Dialog open={detailOpen} onClose={handleCloseDetails} fullWidth maxWidth="sm">
          <DialogTitle>Activity Details</DialogTitle>
          <DialogContent dividers>
            {detailRow ? (
              <>
                <Typography variant="body2" gutterBottom>
                  {new Date(detailRow.createdAt).toLocaleString()}
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {buildDescription(detailRow)}
                </Typography>
                <Box mt={2}>
                  {detailRow.entityType === "invoice" ? (
                    (() => {
                      const invoice = invoiceCache[detailRow.entityId];
                      if (invoiceLoading[detailRow.entityId] && !invoice) {
                        return (
                          <Box display="flex" justifyContent="center" py={2}>
                            <Spinner />
                          </Box>
                        );
                      }
                      if (!invoice) {
                        ensureInvoiceLoaded(detailRow.entityId);
                        return (
                          <Typography variant="body2" color="textSecondary">
                            Loading invoice details...
                          </Typography>
                        );
                      }
                      return (
                        <Box display="flex" flexDirection="column" gap={0.5}>
                          <Typography variant="body2">
                            <strong>Client:</strong>{" "}
                            {invoice?.client?.store || invoice?.client?.name || "Invoice"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Status:</strong> {invoice.status || "Unknown"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Total:</strong> ${Number(invoice.total || 0).toFixed(2)}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Received:</strong>{" "}
                            {invoice.receivedByDate
                              ? new Date(invoice.receivedByDate).toLocaleDateString()
                              : "—"}
                          </Typography>
                          <Typography variant="body2">
                            <strong>Due Date:</strong>{" "}
                            {invoice.dueDate
                              ? new Date(invoice.dueDate).toLocaleDateString()
                              : "—"}
                          </Typography>
                          <Box mt={1}>
                            <Typography variant="body2" style={{ fontWeight: 600 }}>
                              Payments
                            </Typography>
                            {(invoice.paymentRecords || []).length === 0 ? (
                              <Typography variant="body2" color="textSecondary">
                                No payments recorded.
                              </Typography>
                            ) : (
                              (invoice.paymentRecords || []).map((payment, idx) => (
                                <Typography key={idx} variant="body2">
                                  ${Number(payment.amountPaid || 0).toFixed(2)} on{" "}
                                  {payment.datePaid
                                    ? new Date(payment.datePaid).toLocaleDateString()
                                    : "Unknown date"}{" "}
                                  {payment.paymentMethod ? `• ${payment.paymentMethod}` : ""}
                                  {payment.paidBy ? ` • ${payment.paidBy}` : ""}
                                </Typography>
                              ))
                            )}
                          </Box>
                        </Box>
                      );
                    })()
                  ) : (
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {Object.entries(detailRow.metadata || {})
                        .filter(([key]) => key !== "invoiceNumber")
                        .map(([key, value]) => (
                          <Typography key={key} variant="body2">
                            <strong>{key}:</strong>{" "}
                            {typeof value === "object" ? JSON.stringify(value) : String(value)}
                          </Typography>
                        ))}
                    </Box>
                  )}
                </Box>
              </>
            ) : null}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDetails} color="primary">
              Close
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </div>
  );
};

export default ActivityLogPage;
